class VideoPlayer {
  static CLASS_NAME = 'video-player';
  static INTERACTING_TIMEOUT = 3000;

  /**
   *
   * @return {boolean}
   */
  static detectTouchDevice() {
    return (
      ('ontouchstart' in window) ||
      (navigator.maxTouchPoints > 0) ||
      (navigator.msMaxTouchPoints > 0)
    );
  }

  constructor(element) {
    this.$element = element;
    this.init();
  }

  init() {
    this.initProtectedProps();
    this.initBlocks();
    this.initDimensions();
    this.addBallEventListeners();
    this.addElementEventListeners();
    this.addVideoEventListeners();
    this.addClickAreaEventListeners();
    this.addPlayButtonEventListeners();
    this.addTimelineTrackEventListeners();
    this.addFullscreenButtonEventListeners();
    this.addCloseButtonEventListeners();
  }

  initProtectedProps() {
    this.$dragStartX = 0;
    this.$dragStartProgressPercent = 0;
    this.$isControlsPanelVisible = false;
    this.$isFirstInteracted = false;
    this.$isFullscreen = false;
    this.$isInteracting = false;
    this.$isInteractingTimeout = null;
    this.$isLoading = false;
    this.$isPlaying = false;
    this.$isDragging = false;
    this.$prevIsPlaying = false;
    this.$progressPercent = 0;
  }

  initBlocks() {
    this.$videoBlock = this.$element.querySelector(`.${VideoPlayer.CLASS_NAME}__video`);
    this.$clickAreaBlock = this.$element.querySelector(`.${VideoPlayer.CLASS_NAME}__click-area`);
    this.$controlsBlock = this.$element.querySelector(`.${VideoPlayer.CLASS_NAME}__controls`);
    this.$fullscreenButtonBlock = this.$element.querySelector(`.${VideoPlayer.CLASS_NAME}__fullscreen-button`);
    this.$closeButtonBlock = this.$element.querySelector(`.${VideoPlayer.CLASS_NAME}__close-button`);
    this.$playButtonBlock = this.$element.querySelector(`.${VideoPlayer.CLASS_NAME}__play-button`);
    this.$timelineTrackBlock = this.$element.querySelector(`.${VideoPlayer.CLASS_NAME}__timeline-track`);
    this.$timelineProgressBlock = this.$element.querySelector(`.${VideoPlayer.CLASS_NAME}__timeline-progress`);
    this.$timelineBallBlock = this.$element.querySelector(`.${VideoPlayer.CLASS_NAME}__timeline-ball`);
    this.$timelineBallContainerBlock = this.$element.querySelector(`.${VideoPlayer.CLASS_NAME}__timeline-ball-container`);
  }

  initDimensions() {
    if (
        this.$videoBlock.videoWidth &&
        this.$videoBlock.videoHeight
    ) {
      this.setDimensions(this.$videoBlock.videoWidth, this.$videoBlock.videoHeight);
    }
  }

  addBallEventListeners() {
    if (this.$timelineBallBlock) {
      this.$timelineBallBlock.addEventListener('mousedown', (event) => {
        this.onDragStart(event);
      });

      this.$timelineBallBlock.addEventListener('touchstart', (event) => {
        this.onDragStart(event);
      });
    }
  }

  addClickAreaEventListeners() {
    if (this.$clickAreaBlock) {
      this.$clickAreaBlock.addEventListener('click', () => {
        this.onClickAreaClick();
      });
    }
  }

  addCloseButtonEventListeners() {
    if (this.$closeButtonBlock) {
      this.$closeButtonBlock.addEventListener('click', () => {
        this.onCloseButtonClick();
      });
    }
  }

  addDragEventListeners() {
    this.dragListener = this.onDrag.bind(this);
    this.dragEndListener = this.onDragEnd.bind(this);

    if(VideoPlayer.detectTouchDevice()) {
      window.addEventListener('touchmove', this.dragListener);
      window.addEventListener('touchend', this.dragEndListener);
    } else {
      window.addEventListener('mousemove', this.dragListener);
      window.addEventListener('mouseup', this.dragEndListener);
    }
  }

  addElementEventListeners() {
    if (this.$controlsBlock) {
      this.$element.addEventListener('click', () => {
        this.startIsInteractingTimeout();
      });

      this.$element.addEventListener('mousemove', () => {
        this.startIsInteractingTimeout();
      });
    }
  }

  addFullscreenButtonEventListeners() {
    if (this.$fullscreenButtonBlock) {
      this.$fullscreenButtonBlock.addEventListener('click', () => {
        this.onFullscreenButtonClick();
      });
    }
  }

  addPlayButtonEventListeners() {
    if (this.$playButtonBlock) {
      this.$playButtonBlock.addEventListener('click', () => {
        this.onPlayButtonClick();
      });
    }
  }

  addTimelineTrackEventListeners() {
    if (this.$controlsBlock) {
      this.$timelineTrackBlock.addEventListener('click', (event) => {
        this.onTimelineTrackClick(event);
      });
    }
  }

  addVideoEventListeners() {
    this.$videoBlock.addEventListener('loadedmetadata', () => {
      this.onLoadedMetaData();
    });

    this.$videoBlock.addEventListener('play', () => {
      this.onPlay();
    });

    this.$videoBlock.addEventListener('pause', () => {
      this.onPause();
    });

    this.$videoBlock.addEventListener('waiting', () => {
      this.onWaiting();
    });

    this.$videoBlock.addEventListener('canplay', () => {
      this.onCanPlay();
    });
  }

  moveProgressBar() {
    this.$timelineProgressBlock.style.transform = `translateX(${this.progressPercent - 100}%)`;
    this.$timelineBallContainerBlock.style.transform = `translateX(${this.progressPercent - 100}%)`;
  }

  onCanPlay() {
    this.isLoading = false;
  }

  onClickAreaClick() {
    this.togglePlay();
  }

  onCloseButtonClick() {
    this.isFullscreen = false;
  }

  /**
   *
   * @param {PointerEvent|TouchEvent} event
   */
  onDrag(event) {
    if(!this.isDragging) {
      return;
    }

    const clientX = event.touches?.length ? event.touches[0].clientX : event.clientX;
    const x = clientX - this.dragStartX;
    const diffPercent = (x / this.$timelineTrackBlock.getBoundingClientRect().width) * 100;

    this.progressPercent = Math.max(0, Math.min(100, this.dragStartProgressPercent + diffPercent));

    this.moveProgressBar();
  }

  onDragEnd() {
    if (!this.isDragging) {
      return;
    }

    this.rewind(this.progressPercent / 100);
    this.removeDragListeners();

    this.isDragging = false;

    if(this.prevIsPlaying) {
      this.play();
    }
  }

  /**
   *
   * @param {PointerEvent|TouchEvent} event
   */
  onDragStart(event) {
    if (this.isDragging) {
      return;
    }

    const clientX = event.touches?.length ? event.touches[0].clientX : event.clientX;

    this.prevIsPlaying = this.isPlaying;
    this.dragStartX = clientX;
    this.dragStartProgressPercent = this.progressPercent;

    this.pause();
    this.isDragging = true;
    this.addDragEventListeners();
  }

  onFullscreenButtonClick() {
    this.isFullscreen = !this.isFullscreen;
  }

  onLoadedMetaData() {
    this.setDimensions(
      this.$videoBlock.videoWidth,
      this.$videoBlock.videoHeight,
    );
  }

  onPause() {
    this.isPlaying = false;
  }

  onPlay() {
    if (!this.isFirstInteracted) {
      this.isFirstInteracted = true;
    }

    this.isPlaying = true;
    this.progressLoop();
  }

  onPlayButtonClick() {
    this.togglePlay();
  }

  /**
   *
   * @param {PointerEvent} event
   */
  onTimelineTrackClick(event) {
    if (this.isDragging) {
      return;
    }

    const trackClientRect = this.$timelineTrackBlock.getBoundingClientRect();
    const rewindRate = (event.clientX - trackClientRect.left) / trackClientRect.width;

    this.rewind(rewindRate);
  }

  onWaiting() {
    this.isLoading = true;
  }

  pause() {
    this.$videoBlock.pause();
  }

  play() {
    this.$videoBlock
      .play()
      .catch(() => {});
  }

  progressLoop() {
    this.updateProgressPercent();

    if (this.isPlaying) {
      requestAnimationFrame(() => {
        this.progressLoop();
      });
    }
  }

  removeDragListeners() {
    if(VideoPlayer.detectTouchDevice()) {
      window.removeEventListener('touchmove', this.dragListener);
      window.removeEventListener('touchend', this.dragEndListener);
    } else {
      window.removeEventListener('mousemove', this.dragListener);
      window.removeEventListener('mouseup', this.dragEndListener);
    }
  }

  /**
   *
   * @param {number} rate
   */
  rewind(rate) {
    this.$videoBlock.currentTime = this.$videoBlock.duration * rate;
    this.updateProgressPercent();
  }

  /**
   *
   * @param {number} width
   * @param {number} height
   */
  setDimensions(width, height) {
    this.$element.style.paddingBottom = height / width * 100 + '%';
  }

  startIsInteractingTimeout() {
    clearTimeout(this.$isInteractingTimeout);
    
    this.isInteracting = true;

    this.$isInteractingTimeout = setTimeout(() => {
      this.isInteracting = false;
    }, VideoPlayer.INTERACTING_TIMEOUT);
  }

  togglePlay() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  updateIsControlsPanelVisible() {
    this.isControlsPanelVisible = (
      this.isFirstInteracted &&
      (
        this.isInteracting ||
        !this.isPlaying
      )
    );
  }

  updateProgressPercent() {
    if (!this.$videoBlock) {
      return;
    }

    this.progressPercent = (this.$videoBlock.currentTime / this.$videoBlock.duration) * 100;

    this.moveProgressBar();
  }

  // Getters

  /**
   *
   * @return {number}
   */
  get dragStartX() {
    return this.$dragStartX;
  }

  /**
   *
   * @return {number}
   */
  get dragStartProgressPercent() {
    return this.$dragStartProgressPercent;
  }

  /**
   *
   * @return {boolean}
   */
  get isControlsPanelVisible() {
    return this.$isControlsPanelVisible;
  }

  /**
   *
   * @return {boolean}
   */
  get isFirstInteracted() {
    return this.$isFirstInteracted;
  }

  /**
   *
   * @return {boolean}
   */
  get isFullscreen() {
    return this.$isFullscreen;
  }

  /**
   *
   * @return {boolean}
   */
  get isInteracting() {
    return this.$isInteracting;
  }

  /**
   *
   * @return {null|number}
   */
  get isInteractingTimeout() {
    return this.$isInteractingTimeout;
  }

  /**
   *
   * @return {boolean}
   */
  get isLoading() {
    return this.$isLoading;
  }

  /**
   *
   * @return {boolean}
   */
  get isPlaying() {
    return this.$isPlaying;
  }

  /**
   *
   * @return {boolean}
   */
  get isDragging() {
    return this.$isDragging;
  }

  /**
   *
   * @return {boolean}
   */
  get prevIsPlaying() {
    return this.$prevIsPlaying;
  }

  /**
   *
   * @return {number}
   */
  get progressPercent() {
    return this.$progressPercent;
  }


  // Setters
  /**
   *
   * @param {number} value
   */
  set dragStartProgressPercent(value) {
    this.$dragStartProgressPercent = value;
  }

  /**
   *
   * @param {number} value
   */
  set dragStartX(value) {
    this.$dragStartX = value;
  }

  /**
   *
   * @param {boolean} value
   */
  set isControlsPanelVisible(value) {
    this.$isControlsPanelVisible = value;
    this.$element.classList.toggle(`${VideoPlayer.CLASS_NAME}--controls-panel-visible`, value);
  }

  /**
   *
   * @param {boolean} value
   */
  set isDragging(value) {
    this.$isDragging = value;
  }

  /**
   *
   * @param {boolean} value
   */
  set isFirstInteracted(value) {
    this.$isFirstInteracted = value;
    this.$element.classList.toggle(`${VideoPlayer.CLASS_NAME}--first-interacted`, value);
    this.updateIsControlsPanelVisible();
  }

  set isFullscreen(value) {
    this.$isFullscreen = value;
    this.$element.classList.toggle(`${VideoPlayer.CLASS_NAME}--fullscreen`, value);
  }

  /**
   *
   * @param {boolean} value
   */
  set isInteracting(value) {
    this.$isInteracting = value;
    this.$element.classList.toggle(`${VideoPlayer.CLASS_NAME}--interacting`, value);
    this.updateIsControlsPanelVisible();
  }

  /**
   *
   * @param {boolean} value
   */
  set isLoading(value) {
    this.$isLoading = value;
    this.$element.classList.toggle(`${VideoPlayer.CLASS_NAME}--loading`, value);
  }

  /**
   *
   * @param {boolean} value
   */
  set isPlaying(value) {
    this.$isPlaying = value;
    this.$element.classList.toggle(`${VideoPlayer.CLASS_NAME}--playing`, value);
    this.updateIsControlsPanelVisible();
  }

  /**
   *
   * @param {boolean} value
   */
  set prevIsPlaying(value) {
    this.$prevIsPlaying = value;
  }

  /**
   *
   * @param {number} value
   */
  set progressPercent(value) {
    this.$progressPercent = value;
  }

}